/**
 * Chat Real-Time Stream Composable
 *
 * Subscribes to chat:event via Socket.IO and provides
 * callback hooks for each event type. Includes polling fallback
 * when Socket.IO is disconnected.
 */

import { watch, onUnmounted, ref } from 'vue'
import { useSocket } from './useSocket'
import logger from '@/utils/logger'

interface ChatEventPayload {
  event: string
  clientId: string | null
  data: Record<string, any>
  timestamp: string
  source: string
  _subject: string
}

type ChatEventCallback = (_data: ChatEventPayload['data'], _payload: ChatEventPayload) => void

export function useChatStream() {
  const { on, off, emit, isConnected } = useSocket()
  const listeners = ref<Map<string, Set<ChatEventCallback>>>(new Map())

  function addListener(eventType: string, callback: ChatEventCallback) {
    if (!listeners.value.has(eventType)) {
      listeners.value.set(eventType, new Set())
    }
    listeners.value.get(eventType)!.add(callback)
  }

  function removeListener(eventType: string, callback: ChatEventCallback) {
    listeners.value.get(eventType)?.delete(callback)
  }

  function handleChatEvent(payload: ChatEventPayload) {
    const eventType = payload.event
    const callbacks = listeners.value.get(eventType)
    if (callbacks) {
      callbacks.forEach(cb => {
        try {
          cb(payload.data, payload)
        } catch (err) {
          logger.error('[useChatStream] Callback error:', err)
        }
      })
    }

    const wildcardCallbacks = listeners.value.get('*')
    if (wildcardCallbacks) {
      wildcardCallbacks.forEach(cb => {
        try {
          cb(payload.data, payload)
        } catch (err) {
          logger.error('[useChatStream] Wildcard callback error:', err)
        }
      })
    }
  }

  on('chat:event', handleChatEvent)

  onUnmounted(() => {
    off('chat:event', handleChatEvent)
    listeners.value.clear()
  })

  // Polling fallback
  let fallbackTimer: ReturnType<typeof setInterval> | null = null
  let fallbackFn: (() => void) | null = null

  function setFallback(pollFn: () => void, intervalMs = 5000) {
    fallbackFn = pollFn
    watch(isConnected, (connected) => {
      if (!connected && fallbackFn) {
        if (!fallbackTimer) {
          fallbackTimer = setInterval(fallbackFn, intervalMs)
        }
      } else {
        if (fallbackTimer) {
          clearInterval(fallbackTimer)
          fallbackTimer = null
        }
        if (connected && fallbackFn) {
          fallbackFn()
        }
      }
    }, { immediate: true })
  }

  onUnmounted(() => {
    if (fallbackTimer) {
      clearInterval(fallbackTimer)
      fallbackTimer = null
    }
  })

  // Socket.IO emit helpers for agent UI
  function joinChat(sessionId: string) {
    emit('chat:join', { sessionId })
  }

  function leaveChat(sessionId: string) {
    emit('chat:leave', { sessionId })
  }

  function sendMessage(sessionId: string, message: string) {
    emit('chat:send', { sessionId, message })
  }

  function endChat(sessionId: string) {
    emit('chat:end', { sessionId })
  }

  function sendTyping(sessionId: string) {
    emit('chat:typing', { sessionId })
  }

  // Convenience subscription helpers
  function onChatQueued(callback: ChatEventCallback) {
    addListener('chat.queue', callback)
    return () => removeListener('chat.queue', callback)
  }

  function onChatJoined(callback: ChatEventCallback) {
    addListener('chat.joined', callback)
    return () => removeListener('chat.joined', callback)
  }

  function onVisitorMessage(callback: ChatEventCallback) {
    addListener('chat.visitor.msg', callback)
    return () => removeListener('chat.visitor.msg', callback)
  }

  function onAgentMessage(callback: ChatEventCallback) {
    addListener('chat.agent.msg', callback)
    return () => removeListener('chat.agent.msg', callback)
  }

  function onChatTransferred(callback: ChatEventCallback) {
    addListener('chat.transferred', callback)
    return () => removeListener('chat.transferred', callback)
  }

  function onChatEnded(callback: ChatEventCallback) {
    addListener('chat.ended', callback)
    return () => removeListener('chat.ended', callback)
  }

  function onChatTyping(callback: ChatEventCallback) {
    addListener('chat.typing', callback)
    return () => removeListener('chat.typing', callback)
  }

  function onAnyChatEvent(callback: ChatEventCallback) {
    addListener('*', callback)
    return () => removeListener('*', callback)
  }

  return {
    joinChat,
    leaveChat,
    sendMessage,
    endChat,
    sendTyping,
    onChatQueued,
    onChatJoined,
    onVisitorMessage,
    onAgentMessage,
    onChatTransferred,
    onChatEnded,
    onChatTyping,
    onAnyChatEvent,
    setFallback,
    isConnected,
  }
}
